import KdiagramCalculationData from "../../domain/kdiagram/calculation/data.js";
import { kdiagramProfilesModule } from "./profiles.js";
import { KdiagramService } from "../../services/kdiagram/service.js";
import mapKdiagramCalculationResultDataFromJson from "../../domain/kdiagram/calculation_result/mapper.js";
import mapKdiagramCalculationDataToJson from "../../domain/kdiagram/calculation/mapper_to_json.js";
import { mapKDiagramStaticDataFromJson, mapSeaTrialsDataFromJson } from "../../domain/kdiagram/static/mapper.js";
import { _getChartOptionsForTableData } from "./helper_functions/table_result_chart/options.js";
import {
  _getSeriesFromTableData,_generateInterpolationLineSeries,
  _generateRangeLineDataSeries, _generateSeaTrialsSeries } from "./helper_functions/generate_series.js";
import { generateKDiagramChartOptions } from "./helper_functions/kdiagram_chart/options.js";

const kdiagramService = new KdiagramService(
  localStorage.getItem("basicUrl"),
  mapKdiagramCalculationResultDataFromJson,
  mapKdiagramCalculationDataToJson,
  mapKDiagramStaticDataFromJson,
  mapSeaTrialsDataFromJson
);

const getDefaultState = () => ({
  calculationData: {
    ...KdiagramCalculationData.defaultData(),
    useLatestFouling: false,
  },
  calculationType: "input",
  calculationLoading: true,
  calculationError: null,
  calculationResult: null,
  kDiagramStaticData: null,
  kDiagramSeaTrialsData: null,
});

const state = getDefaultState();

const getters = {
  valueTableData: (state) => {
    if (state.calculationResult) {
      return state.calculationResult.valuesTableData;
    }
    return null;
  },
  speedsOfInterestIndexes: (state) => {
    try {
      const speedsOfInterest = state.calculationData.speedThroughWater.split(",").map(Number);
      const speeds = state.calculationResult.valuesTableData.speed;
      const speedsOfInterestIndexes = [];
      for (let i = 0; i < speedsOfInterest.length; i++) {
        const index = speeds.indexOf(speedsOfInterest[i]);
        if (index !== -1) {
          speedsOfInterestIndexes.push(index);
        }
      }
      return speedsOfInterestIndexes;
    } catch (_) {
      return [];
    }
  },
  auxiliaryConsumption: (_, getters) => {
    if (getters.valueTableData) {
      return getters.valueTableData.auxiliaryConsumption;
    }
    return 0;
  },
  speedsOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes;
    const speeds = getters.valueTableData ? getters.valueTableData.speed : [];
    return speeds.filter((_, index) => speedsOfInterestIndexes.includes(index));
  },
  powerOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes;
    const power = getters.valueTableData ? getters.valueTableData.power : [];
    return power.filter((_, index) => speedsOfInterestIndexes.includes(index));
  },
  consumptionOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes;
    const consumption = getters.valueTableData ? getters.valueTableData.consumption : [];
    return consumption.filter((_, index) => speedsOfInterestIndexes.includes(index));
  },
  isoDailyConsumptionOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes;
    const isoDailyConsumption = getters.valueTableData ? getters.valueTableData.isoDailyConsumption : [];
    return isoDailyConsumption.filter((_, index) => speedsOfInterestIndexes.includes(index));
  },
  rpmOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes;
    const rpm = getters.valueTableData ? getters.valueTableData.rpm : [];
    return rpm.filter((_, index) => speedsOfInterestIndexes.includes(index));
  },
  speedPowerSeriesOfInterest: (_, getters) => {
    const speedsOfInterest = getters.speedsOfInterest;
    const powerOfInterest = getters.powerOfInterest;
    return speedsOfInterest.map((speed, index) => [speed, powerOfInterest[index]]);
  },
  speedConsumptionSeriesOfInterest: (_, getters) => {
    const speedsOfInterest = getters.speedsOfInterest;
    const isoDailyConsumptionOfInterest = getters.isoDailyConsumptionOfInterest;
    return speedsOfInterest.map((speed, index) => [speed, isoDailyConsumptionOfInterest[index]]);
  },
  rpmConsumptionSeriesOfInterest: (_, getters) => {
    const rpmOfInterest = getters.rpmOfInterest;
    const isoDailyConsumptionOfInterest = getters.isoDailyConsumptionOfInterest;
    return rpmOfInterest.map((rpm, index) => [rpm, isoDailyConsumptionOfInterest[index]]);
  },
  powerConsumptionSeriesOfInterest: (_, getters) => {
    const powerOfInterest = getters.powerOfInterest;
    const isoDailyConsumptionOfInterest = getters.isoDailyConsumptionOfInterest;
    return powerOfInterest.map((power, index) => [power, isoDailyConsumptionOfInterest[index]]);
  },
  rpmPowerSeriesOfInterest: (_, getters) => {
    const rpmOfInterest = getters.rpmOfInterest;
    const powerOfInterest = getters.powerOfInterest;
    return rpmOfInterest.map((rpm, index) => [rpm, powerOfInterest[index]]);
  },
  rpmSpeedSeriesOfInterest: (_, getters) => {
    const rpmOfInterest = getters.rpmOfInterest;
    const speedOfInterest = getters.speedsOfInterest;
    return rpmOfInterest.map((rpm, index) => [rpm, speedOfInterest[index]]);
  },
  measuredIsoConsumptionOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes || [];
    const measuredIsoConsumption =
            getters.valueTableData &&
                Array.isArray(getters.valueTableData.measuredIsoConsumption)
              ? getters.valueTableData.measuredIsoConsumption
              : [];

    return measuredIsoConsumption.filter((_, index) =>
      speedsOfInterestIndexes.includes(index)
    );
  },

  medianMeasuredConsumptionOfInterest: (_, getters) => {
    const speedsOfInterestIndexes = getters.speedsOfInterestIndexes || [];
    const medianMeasuredConsumption =
            getters.valueTableData &&
                Array.isArray(getters.valueTableData.medianMeasuredConsumption)
              ? getters.valueTableData.medianMeasuredConsumption
              : [];

    return medianMeasuredConsumption.filter((_, index) =>
      speedsOfInterestIndexes.includes(index)
    );
  },

  // ME Daily FOC getters - USE FDM DATA, NOT VALUES_TABLE
  meDailyFOCOfInterest: (_, getters) => {
    // Can't filter FDM array by values_table indexes - just return empty or all FDM data
    const fdm = getters.fdmData;
    return fdm && fdm.Consumption ? fdm.Consumption : [];
  },

  KdiagramMEDailyFOCPower: (_, getters) => {
    const fdm = getters.fdmData || {};
    const power = fdm.mePower || [];
    const meDailyFOC = fdm.Consumption || [];  // From FDM, not values_table
    return power.map((p, i) => [p, meDailyFOC[i]]);
  },

  KdiagramMEDailyFOCSpeed: (_, getters) => {
    const fdm = getters.fdmData || {};
    const speed = fdm.logSpeed || [];
    const meDailyFOC = fdm.Consumption || [];  // From FDM, not values_table
    return speed.map((s, i) => [s, meDailyFOC[i]]);
  },

  KdiagramMEDailyFOCRpm: (_, getters) => {
    const fdm = getters.fdmData || {};
    const rpm = fdm.meRpm || [];
    const meDailyFOC = fdm.Consumption || [];  // From FDM, not values_table
    return rpm.map((r, i) => [r, meDailyFOC[i]]);
  },

  powerMEDailyFOCSeriesOfInterest: (_, getters) => {
    const fdm = getters.fdmData || {};
    if (!fdm.mePower || !fdm.Consumption) return [];
    return getters.KdiagramMEDailyFOCPower;  // Use full FDM curve
  },

  speedMEDailyFOCSeriesOfInterest: (_, getters) => {
    const fdm = getters.fdmData || {};
    if (!fdm.logSpeed || !fdm.Consumption) return [];
    return getters.KdiagramMEDailyFOCSpeed;  // Use full FDM curve
  },

  rpmMEDailyFOCSeriesOfInterest: (_, getters) => {
    const fdm = getters.fdmData || {};
    if (!fdm.meRpm || !fdm.Consumption) return [];
    return getters.KdiagramMEDailyFOCRpm;  // Use full FDM curve
  },
  fdmData: (state) => {
    if (state.calculationResult) {
      const fdm = state.calculationResult.fdmData;

      return fdm;
    }
    return null;
  },

  lineParameters: (state) => {
    if (state.calculationResult) {
      return state.calculationResult.lineParametersData;
    }
    return null;
  },
  rpmPowerSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.valueTableData, "rpm", "power", getters.powerOfInterest);
  },

  speedPowerSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.valueTableData, "speed", "power");
  },

  powerConsumptionSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.valueTableData, "power", "isoDailyConsumption");
  },

  speedConsumptionSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.valueTableData, "speed", "isoDailyConsumption");
  },

  rpmConsumptionSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.valueTableData, "rpm", "isoDailyConsumption");
  },
  rpmPowerChartOptions: (_, getters) => {
    const xMin = getters.valueTableData ? getters.valueTableData.rpm[0] - 2 : 0;
    const xMax = getters.valueTableData ? getters.valueTableData.rpm[getters.valueTableData.rpm.length - 1] + 2 : 0;
    return _getChartOptionsForTableData(
      "",
      "RPM",
      "POWER (kW)",
      getters.rpmPowerSeries,
      getters.rpmPowerSeriesOfInterest,
      getters.kDiagramPowerSeries,   // Data points
      xMin,
      xMax,
      "rpm: {point.x} <br/> Power: {point.y}",
      getters.kDiagramSeaTrialsRpmPowerSeries,
      "Power-RPM Curve"
    );
  },
  speedPowerChartOptions: (_, getters) => {
    const xMin = getters.valueTableData ? getters.valueTableData.speed[0] - 2 : 0;
    const xMax = getters.valueTableData ? getters.valueTableData.speed[getters.valueTableData.speed.length - 1] + 2 : 0;
    return _getChartOptionsForTableData(
      "",
      "SPEED (kn)",
      "POWER (kW)",
      getters.speedPowerSeries,
      getters.speedPowerSeriesOfInterest,           // Evaluation points
      getters.KdiagramPowerSpeedSeries,  // Data points
      xMin,
      xMax,
      "Speed: {point.x} <br/> Power: {point.y}",
      getters.kDiagramSeaTrialsSpeedPowerSeries,
      "SPEED-POWER Curve"

    );
  },
  powerConsumptionChartOptions: (_, getters) => {
    const xMin = getters.valueTableData ? getters.valueTableData.power[0] - 2000 : 0;
    const xMax = getters.valueTableData ? getters.valueTableData.power[getters.valueTableData.power.length - 1] + 2000 : 0;
    return _getChartOptionsForTableData(
      "",
      "POWER (kW)",
      "ME DAILY FOC (tn/day)",
      getters.powerConsumptionSeries,
      getters.powerConsumptionSeriesOfInterest,
      getters.KdiagramMEDailyFOCPower,
      xMin,
      xMax,
      "Power: {point.x} <br/> ME Daily FOC: {point.y}",
      getters.kDiagramSeaTrialsPowerBsfcSeries,
      "ME Daily FOC-Power Curve"
    );
  },

  speedConsumptionChartOptions: (_, getters) => {
    const xMin = getters.valueTableData ? getters.valueTableData.speed[0] - 2 : 0;
    const xMax = getters.valueTableData ? getters.valueTableData.speed[getters.valueTableData.speed.length - 1] + 2 : 0;
    return _getChartOptionsForTableData(
      "",
      "SPEED (kn)",
      "ME DAILY FOC (tn/day)",
      getters.speedConsumptionSeries,
      getters.speedConsumptionSeriesOfInterest,
      getters.KdiagramMEDailyFOCSpeed,
      xMin,
      xMax,
      "Speed: {point.x} <br/> ME Daily FOC: {point.y}",
      getters.kDiagramSeaTrialsSpeedBsfcSeries,
      "ME Daily FOC-Speed Curve"
    );
  },

  rpmConsumptionChartOptions: (_, getters) => {
    const xMin = getters.valueTableData ? getters.valueTableData.rpm[0] - 10 : 0;
    const xMax = getters.valueTableData ? getters.valueTableData.rpm[getters.valueTableData.rpm.length - 1] + 10 : 0;
    return _getChartOptionsForTableData(
      "",
      "RPM",
      "ME DAILY FOC (tn/day)",
      getters.rpmConsumptionSeries,
      getters.rpmConsumptionSeriesOfInterest,
      getters.KdiagramMEDailyFOCRpm,
      xMin,
      xMax,
      "rpm: {point.x} <br/> ME Daily FOC: {point.y}",
      getters.kDiagramSeaTrialsRpmBsfcSeries,
      "ME Daily FOC-RPM Curve"
    );
  },

  kDiagramPowerSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.fdmData, "meRpm", "mePower");
  },

  kDiagramSpeedSeries: (_, getters) => {
    return _getSeriesFromTableData(getters.fdmData, "meRpm", "logSpeed");
  },

  KdiagramPowerSpeedSeries: (_, getters) => {
    const fdm = getters.fdmData;

    if (
      !fdm ||
            !fdm.logSpeed ||
            !fdm.mePower ||
            fdm.logSpeed.length === 0
    ) {
      return [];
    }

    return _getSeriesFromTableData(
      fdm,
      "logSpeed",
      "mePower"
    );
  },

  kDiagramPowerRangeSeries: (state) => {
    return _generateRangeLineDataSeries(
      state.kDiagramStaticData.rangeData, state.kDiagramStaticData.aParameterPowerRangeUpper,
      state.kDiagramStaticData.bParameterPowerRangeUpper, state.kDiagramStaticData.aParameterPowerRangeLower,
      state.kDiagramStaticData.bParameterPowerRangeLower);
  },

  kDiagramSpeedRangeSeries: (state) => {
    return _generateRangeLineDataSeries(
      state.kDiagramStaticData.rangeData, state.kDiagramStaticData.aParameterSpeedRangeUpper,
      state.kDiagramStaticData.bParameterSpeedRangeUpper, state.kDiagramStaticData.aParameterSpeedRangeLower,
      state.kDiagramStaticData.bParameterSpeedRangeLower);
  },

  kDiagramShoptestSeries: (state) => {
    const rpm = state.kDiagramStaticData.shoptestData.rpm;
    const power = state.kDiagramStaticData.shoptestData.power;
    return rpm.map((rpm, index) => [rpm, power[index]]);
  },
  kDiagramSeaTrialsRpmPowerSeries: (state) => {
    return _generateSeaTrialsSeries(state.kDiagramSeaTrialsData, "rpm", "power");
  },
  kDiagramSeaTrialsRpmSpeedSeries: (state) => {
    return _generateSeaTrialsSeries(state.kDiagramSeaTrialsData, "rpm", "speed");
  },
  kDiagramSeaTrialsSpeedPowerSeries: (state) => {
    return _generateSeaTrialsSeries(state.kDiagramSeaTrialsData, "speed", "power");
  },
  kDiagramSeaTrialsSpeedBsfcSeries: (state) => {
    return _generateSeaTrialsSeries(state.kDiagramSeaTrialsData, "speed", "bsfc");
  },
  kDiagramSeaTrialsRpmBsfcSeries: (state) => {
    return _generateSeaTrialsSeries(state.kDiagramSeaTrialsData, "rpm", "bsfc");
  },
  kDiagramSeaTrialsPowerBsfcSeries: (state) => {
    return _generateSeaTrialsSeries(state.kDiagramSeaTrialsData, "power", "bsfc");
  },

  rpmPowerCurveSeries: (_, getters) => {
    return getters.rpmPowerSeries;
  },

  rpmSpeedCurveSeries: (_, getters) => {
    return getters.rpmSpeedSeriesDense;
  },

  kDiagramPowerChartOptions: (_, getters) => {
    const minRpm = getters.fdmData ? Math.min(...getters.valueTableData.rpm) - 10 : null;
    const maxRpm = getters.fdmData ? Math.max(...getters.valueTableData.rpm) + 10 : null;

    //const PowerSeries = getters.rpmPowerSeries
    const Curve = getters.rpmPowerCurveSeries;
    //const Interpolation = getters.kDiagramPowerInterpolationLineSeries;
    const DataPonints = getters.kDiagramPowerSeries;
    const Shoptest = getters.kDiagramShoptestSeries;
    const SeaTrials = getters.kDiagramSeaTrialsRpmPowerSeries;
    const Interest = getters.rpmPowerSeriesOfInterest;

    return generateKDiagramChartOptions(
      "Engine Speed [rpm]",
      "Engine Power [kW]",

      "Power curve",
      "Evaluation Points",
      "Power range",
      "Shoptest",
      "Sea trials",
      "Data points",
      DataPonints,
      Curve,
      null, //Range
      Shoptest,
      SeaTrials,
      Interest,  //Evaluation Point
      minRpm,
      maxRpm,
      false,
      "top",
      "Power: {point.y} kW <br/> Engine Speed: {point.x} rpm <br />"
    );
  },

  //Speed Points
  rpmSpeedSeriesDense: (_, getters) => {
    const table = getters.valueTableData || {};
    const rpm = table.rpm || [];
    const speed = table.speed || [];

    return rpm.map((r, i) => [r, speed[i]]);
  },

  kDiagramSpeedChartOptions: (_, getters) => {
    const minRpm = getters.fdmData ? Math.min(...getters.valueTableData.rpm) - 10 : 0;
    const maxRpm = getters.fdmData ? Math.max(...getters.valueTableData.rpm) + 10 : 0;

    const SpeedSeries = getters.kDiagramSpeedSeries;
    // const Interpolation = getters.kDiagramSpeedInterpolationLineSeries;
    const Curve = getters.rpmSpeedCurveSeries;
    //const Range = getters.rpmSpeedSeriesDense;
    const SeaTrials = getters.kDiagramSeaTrialsRpmSpeedSeries;
    const Interest = getters.rpmSpeedSeriesOfInterest;

    return generateKDiagramChartOptions(
      "Engine Speed [rpm]",  //x axis
      "Speed [kn]",          //y axis

      "Speed curve",         //name 1
      "Evaluation points",        //name 2
      "Speed range",         //name 3

      null,                 //dont have shoptest here
      "Sea trials",          //name 4
      "Data points",         //name 5

      SpeedSeries,           //data    (meRpm', 'logSpeed)
      //Interpolation,         //interpolationData  (calculated with line parameter A,B)
      Curve,
      //Range,                 //rangeData
      null,
      null,                  //no shoptestData
      SeaTrials,             //seaTrialsData
      Interest,              //predictionData
      minRpm,                //resultsMinRpm
      maxRpm,                //resultsMaxRpm
      true,                  //reversed
      "bottom",
      "Speed: {point.y} kn <br/> Engine Speed: {point.x} rpm <br />"
    );
  },

};

const actions = {

  async calculateByPreset({ commit, rootState }, data) {

    commit("SET_CALCULATION_LOADING_STATE");

    const profileSpeedThroughWater = rootState.kDiagram.profiles.selectedProfile.valuesList;
    if (profileSpeedThroughWater) {
      commit("SET_SPEED_THROUGH_WATER", profileSpeedThroughWater);
    }

    try {
      const response = await kdiagramService.calculateByPreset(
        localStorage.getItem("quad_token"),
        data.vessel,
        data.profileId,
      );
      commit("SET_CALCULATION_SUCCESS_STATE", response);
    } catch (error) {
      commit("SET_CALCULATION_FAILED_STATE", error);
    }
  },

  async calculate({ commit, state }, payload) {
    commit("SET_CALCULATION_LOADING_STATE");

    try {
      const vesselName =
      typeof payload === "string"
        ? payload
        : payload.vessel;

      const calculationData =
      typeof payload === "string"
        ? state.calculationData
        : {
          ...state.calculationData,
          meanDraft: payload.meanDraft,
          trim: payload.trim,
          trueWindSpeed: payload.trueWindSpeed,
        };

      const response = await kdiagramService.calculate(
        localStorage.getItem("quad_token"),
        vesselName,
        calculationData
      );

      commit("SET_CALCULATION_SUCCESS_STATE", response);

      return response;
    } catch (error) {
      commit("SET_CALCULATION_FAILED_STATE", error);
      throw error;
    }
  },

  async fetchKDiagramSeaTrials({ commit }, vesselName) {
    const response = await kdiagramService.fetchKDiagramSeaTrials(
      localStorage.getItem("quad_token"),
      vesselName
    );
    commit("SET_K_DIAGRAM_SEA_TRIALS_DATA", response);
  },

  async fetchKDiagramStaticData({ commit }, vesselName) {
    const response = await kdiagramService.fetchKDiagramStaticData(
      localStorage.getItem("quad_token"),
      vesselName
    );
    commit("SET_K_DIAGRAM_STATIC_DATA", response);
  },

  async fetchLatestFouling({ commit }, vesselName) {
    const token = localStorage.getItem("quad_token");
    const fouling = await kdiagramService.fetchLatestFouling(token, vesselName);
    const value = fouling !== null ? fouling : 0;
    commit("SET_FOULING", value);
    return value;
  }
};

const mutations = {
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_CALCULATION_DATA: (state, data) => {
    state.calculationData = data;
  },
  SET_CALCULATION_TYPE: (state, data) => {
    state.calculationType = data;
  },
  SET_SPEED_THROUGH_WATER: (state, data) => {
    state.calculationData.speedThroughWater = data;
  },

  SET_LOWER_HEATING_VALUE: (state, data) => {
    state.calculationData.lowerHeatingValue = data;
  },
  SET_MEAN_DRAFT: (state, data) => {
    state.calculationData.meanDraft = data;
  },
  SET_TRIM: (state, data) => {
    state.calculationData.trim = data;
  },
  SET_TRUE_WIND_SPEED: (state, data) => {
    state.calculationData.trueWindSpeed = data;
  },
  SET_WIND_DIRECTION: (state, data) => {
    state.calculationData.windDirection = data;
  },
  SET_FOULING: (state, data) => {
    state.calculationData.fouling = data;
  },
  SET_DATE: (state, data) => {
    state.calculationData.date = data;
  },
  SET_SPEED_RANGE: (state, data) => {
    state.calculationData.speedRange = data;

    // We want when speed range is set, the points of interest to be set accordingly
    const lowerSpeed = Math.floor(Math.max(data[0], 7));
    const upperSpeed = Math.ceil(Math.min(data[1], 15));
    state.calculationData.speedThroughWater = Array.from({ length: upperSpeed - lowerSpeed + 1 }, (_, i) => i + lowerSpeed).join(",");
  },
  SET_DATE_RANGE: (state, data) => {
    state.calculationData.minDate = data[0];
    state.calculationData.maxDate = data[1];
  },
  SET_CALCULATION_DATA_ACCORDING_TO_PROFILE: (state, data) => {
    state.calculationData.speedThroughWater = data.valuesList;
    state.calculationData.lowerHeatingValue = data.lhv;
    state.calculationData.meanDraft = Number(data.draft.toFixed(2));
    state.calculationData.trim = Number(data.trim.toFixed(3));
    state.calculationData.trueWindSpeed = data.trueWind;

    state.calculationData.useLatestFouling = data.use_latest_fouling;

    const speeds = data.valuesList.split(",").map(Number);
    const minSpeed = Math.min(...speeds);
    const maxSpeed = Math.max(...speeds);
    state.calculationData.speedRange = [Math.max(minSpeed, 7), Math.min(maxSpeed, 15)];
  },
  SET_CALCULATION_LOADING_STATE: (state) => {
    state.calculationLoading = true;
    state.calculationError = null;
  },
  SET_CALCULATION_FAILED_STATE: (state, error) => {
    state.calculationLoading = false;
    state.calculationError = error;
  },
  SET_CALCULATION_SUCCESS_STATE: (state, data) => {
    state.calculationLoading = false;
    state.calculationError = null;
    state.calculationResult = data;
  },
  SET_K_DIAGRAM_STATIC_DATA: (state, data) => {
    state.kDiagramStaticData = data;
  },
  SET_K_DIAGRAM_SEA_TRIALS_DATA: (state, data) => {
    state.kDiagramSeaTrialsData = data;
  },
  SET_USE_LATEST_FOULING(state, value) {
    state.calculationData.useLatestFouling = value;
  }
};

export const kdiagramCalculateModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    profiles: kdiagramProfilesModule
  }
};
